import React from 'react';
import { Link } from 'react-router-dom';

interface CostsData {
  mtd: number;
  projected: number;
  error: string | null;
}

interface CostsOverviewCardProps {
  costs: CostsData;
  loading?: boolean;
  budget?: number;
}

function CostsOverviewCard({ costs, loading, budget = 50 }: CostsOverviewCardProps) {
  const percentUsed = (costs.mtd / budget) * 100;

  // Color based on budget usage
  const getProgressColor = () => {
    if (percentUsed > 80) return 'bg-mc-danger';
    if (percentUsed > 60) return 'bg-mc-warning';
    return 'bg-mc-success';
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Costs (MTD)</h3>
          <div className="animate-pulse bg-mc-border h-4 w-12 rounded"></div>
        </div>
        <div className="animate-pulse bg-mc-border h-10 w-24 rounded mb-2"></div>
        <div className="animate-pulse bg-mc-border h-4 w-32 rounded"></div>
        <div className="mt-4 pt-4 border-t border-mc-border">
          <div className="animate-pulse bg-mc-border h-4 w-full rounded mb-2"></div>
          <div className="animate-pulse bg-mc-border h-2 w-full rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Costs (MTD)</h3>
        <Link to="/costs" className="text-mc-accent text-sm hover:underline">
          Details
        </Link>
      </div>

      <div className={`text-4xl font-bold ${getProgressColor().replace('bg-', 'text-')}`}>
        ${costs.mtd.toFixed(2)}
      </div>
      <p className="text-mc-muted text-sm mt-2">
        {costs.error ? (
          <span className="text-mc-warning">{costs.error}</span>
        ) : (
          <>Projected: ${costs.projected.toFixed(2)} / ${budget.toFixed(2)}</>
        )}
      </p>

      {/* Budget Progress Bar */}
      <div className="mt-4 pt-4 border-t border-mc-border">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-mc-muted">Budget Used</span>
          <span>{percentUsed.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-mc-border rounded-full h-2">
          <div
            className={`${getProgressColor()} rounded-full h-2 transition-all duration-500`}
            style={{ width: `${Math.min(percentUsed, 100)}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
}

export default CostsOverviewCard;
